/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, DOCUMENT, Inject, signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export type CommunityProfileEditMode = 'avatar' | 'banner';

export interface ProfileEditDialogData {
  mode: CommunityProfileEditMode;
}

export type CommunityProfileAvatarSource = 'file' | 'gravatar';

// Wording and limits match Tester App > Account Settings, which edits the same data. The size limits are
// the API's (PUT /api/v1/users/me: avatar 2MB, profile_banner 5MB).
const COPY: Record<CommunityProfileEditMode, { title: string; hint: string; maxBytes: number }> = {
  avatar: {
    title: 'Update Avatar',
    hint: 'Avatars should be square in shape and a maximum of 300px wide and 300px tall. Acceptable file types are .jpg, .jpeg, .gif, and .png.',
    maxBytes: 2 * 1024 * 1024,
  },
  banner: {
    title: 'Update Banner',
    hint: 'Use a wide image, at least 1500×300px, with anything important near the middle: the edges are cropped on smaller screens. Acceptable file types are .jpg, .jpeg, .gif, and .png.',
    maxBytes: 5 * 1024 * 1024,
  },
};

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'gif', 'png'];

// The legacy profile's avatar and banner overlays, in the ngf dialog (title with a close button,
// content and actions).
// Saving goes through UserService.updateMe like before, then reloads so every avatar on the page updates.
@Component({
  selector: 'ut-community-profile-edit-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  templateUrl: './community-profile-edit-dialog.component.html',
  styleUrls: ['../shared/community-profile-dialog.scss'],
})
export class CommunityProfileEditDialogComponent {
  readonly accept = '.jpg,.jpeg,.gif,.png';
  readonly hintId = 'profileDialogHint';

  file: File | null = null;
  gravatar = '';
  source: CommunityProfileAvatarSource = 'file';
  saving = signal(false);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProfileEditDialogData,
    @Inject('UserService') private UserService: any,
    @Inject('AlertService') private AlertService: any,
    @Inject(DOCUMENT) private document: Document,
    private dialogRef: MatDialogRef<CommunityProfileEditDialogComponent>,
  ) {}

  get copy(): { title: string; hint: string; maxBytes: number } {
    return COPY[this.data.mode];
  }

  get usesGravatar(): boolean {
    return this.data.mode === 'avatar' && this.source === 'gravatar';
  }

  get maxSizeLabel(): string {
    return `${this.copy.maxBytes / 1024 / 1024} MB`;
  }

  // Checked here so the tester sees why, instead of the API's rejection (which is easy to miss).
  get fileError(): 'type' | 'size' | null {
    if (!this.file) return null;
    const extension = this.file.name.includes('.') ? this.file.name.split('.').pop()!.toLowerCase() : '';
    if (!ALLOWED_EXTENSIONS.includes(extension)) return 'type';
    return this.file.size > this.copy.maxBytes ? 'size' : null;
  }

  get canSave(): boolean {
    if (this.usesGravatar) return !!this.gravatar.trim();
    return !!this.file && !this.fileError;
  }

  pick(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file = input.files?.[0] || null;
  }

  clearFile(input: HTMLInputElement): void {
    input.value = '';
    this.file = null;
  }

  save(): void {
    if (!this.canSave) return;
    this.saving.set(true);
    this.UserService.updateMe(this.changes()).then(() => {
      this.AlertService.add('Changes successfully saved!', 'success');
      this.dialogRef.close();
      this.document.location.reload();
    }, (err) => {
      this.saving.set(false);
      this.AlertService.addDanger(err?.data?.errors?.[0]?.title || 'There was an error');
    });
  }

  private changes(): Record<string, unknown> {
    switch (this.data.mode) {
      case 'avatar':
        return this.usesGravatar ? { gravatar: this.gravatar.trim() } : { avatar: this.file };
      default:
        return { profile_banner: this.file };
    }
  }
}
